/* propCatalog — a MARKET-FIRST presentation transform for player props.

   The backend keeps returning `props_by_player` (player → outcomes). That structure is still the
   source of truth for betting and for the SGP builder, and is NOT changed here. This module derives a
   read-only browsing catalog on top of it so the Player Props UI can be organised as:

        Category  →  Market  →  Players  →  available lines / thresholds

   instead of one card per player. The SAME outcome objects are carried through unchanged (same id,
   market_version, odds, line, side, suspended/active, provider timestamps) — we only regroup them for
   display, so bet placement, freshness and suspension behaviour are identical.

   Alternate thresholds (e.g. a provider offering Receiving Yards at 39.5 / 49.5 / 59.5 … over-only)
   are grouped under their market as "50+ / 60+ …" chips. We NEVER fabricate a threshold or synthesise a
   market — only real provider outcomes are rendered. Standard O/U stays O/U; Yes/No stays Yes/No. */

import { normalizeSport } from "./sportsbookPriority";

// ---- Category definitions per sport -----------------------------------------------------
// Each sport has an ordered list of categories. "Popular" and "Other" are implicit/common.
const CATEGORY_ORDER = {
  football: ["popular", "touchdowns", "passing", "rushing", "receiving", "kicking", "other"],
  basketball: ["popular", "points", "rebounds", "assists", "threes", "combos", "defense", "other"],
  baseball: ["popular", "pitching", "hitting", "other"],
  hockey: ["popular", "goals", "points", "shots", "assists", "goalie", "other"],
  default: ["popular", "other"],
};

const CATEGORY_LABEL = {
  popular: "Popular", touchdowns: "Touchdowns", passing: "Passing", rushing: "Rushing",
  receiving: "Receiving", kicking: "Kicking", points: "Points", rebounds: "Rebounds",
  assists: "Assists", threes: "Threes", combos: "Combos", defense: "Defense",
  pitching: "Pitching", hitting: "Hitting", goals: "Goals", shots: "Shots",
  goalie: "Goalie", other: "Other",
};

// prop_type -> { cats: [category keys the market belongs to], pop: boolean (is it a "Popular" market),
//               order: within-category market order (lower first) }. A market may belong to several
//               categories (e.g. a passing-TD market lives under both Passing and Touchdowns).
// Keyed by normalized sport then prop_type.
const PROP_MAP = {
  football: {
    anytime_touchdown_scorer: { cats: ["touchdowns"], pop: true, order: 0 },
    first_touchdown_scorer: { cats: ["touchdowns"], order: 2 },
    last_touchdown_scorer: { cats: ["touchdowns"], order: 3 },
    player_passing_yards: { cats: ["passing"], pop: true, order: 0 },
    player_passing_touchdowns: { cats: ["passing", "touchdowns"], pop: true, order: 1 },
    player_passing_attempts: { cats: ["passing"], order: 4 },
    player_passing_completions: { cats: ["passing"], order: 5 },
    player_interceptions: { cats: ["passing"], order: 6 },
    player_longest_passing_completion: { cats: ["passing"], order: 7 },
    player_rushing_yards: { cats: ["rushing"], pop: true, order: 0 },
    player_rushing_attempts: { cats: ["rushing"], order: 2 },
    player_rushing_touchdowns: { cats: ["rushing", "touchdowns"], order: 3 },
    player_longest_rush: { cats: ["rushing"], order: 4 },
    "player_rushing_+_receiving_yards": { cats: ["rushing", "receiving"], order: 5 },
    player_rushing_receiving_yards: { cats: ["rushing", "receiving"], order: 5 },
    "player_passing_+_rushing_yards": { cats: ["passing", "rushing"], order: 6 },
    player_passing_rushing_yards: { cats: ["passing", "rushing"], order: 6 },
    player_receiving_yards: { cats: ["receiving"], pop: true, order: 0 },
    player_receptions: { cats: ["receiving"], pop: true, order: 1 },
    player_receiving_touchdowns: { cats: ["receiving", "touchdowns"], order: 3 },
    player_longest_reception: { cats: ["receiving"], order: 4 },
    player_kicking_points: { cats: ["kicking"], order: 0 },
    player_field_goals_made: { cats: ["kicking"], order: 1 },
    player_extra_points_made: { cats: ["kicking"], order: 2 },
  },
  basketball: {
    player_points: { cats: ["points"], pop: true, order: 0 },
    player_rebounds: { cats: ["rebounds"], pop: true, order: 0 },
    player_assists: { cats: ["assists"], pop: true, order: 0 },
    player_threes: { cats: ["threes"], pop: true, order: 0 },
    player_three_point_field_goals_made: { cats: ["threes"], pop: true, order: 0 },
    player_made_threes: { cats: ["threes"], pop: true, order: 0 },
    player_points_rebounds_assists: { cats: ["combos"], pop: true, order: 0 },
    player_points_rebounds: { cats: ["combos"], order: 1 },
    player_points_assists: { cats: ["combos"], order: 2 },
    player_rebounds_assists: { cats: ["combos"], order: 3 },
    player_steals: { cats: ["defense"], order: 0 },
    player_blocks: { cats: ["defense"], order: 1 },
    player_turnovers: { cats: ["defense"], order: 2 },
    most_points_player: { cats: ["points"], order: 9 },
  },
  baseball: {
    player_strikeouts: { cats: ["pitching"], pop: true, order: 0 },
    player_pitching_strikeouts: { cats: ["pitching"], pop: true, order: 0 },
    player_hits_allowed: { cats: ["pitching"], order: 2 },
    player_earned_runs: { cats: ["pitching"], order: 3 },
    player_hits: { cats: ["hitting"], pop: true, order: 0 },
    player_total_bases: { cats: ["hitting"], pop: true, order: 1 },
    player_home_runs: { cats: ["hitting"], pop: true, order: 2 },
    player_rbis: { cats: ["hitting"], order: 3 },
    player_runs: { cats: ["hitting"], order: 4 },
  },
  hockey: {
    player_goals: { cats: ["goals"], pop: true, order: 0 },
    anytime_goal_scorer: { cats: ["goals"], pop: true, order: 1 },
    player_points: { cats: ["points"], pop: true, order: 0 },
    player_shots_on_goal: { cats: ["shots"], pop: true, order: 0 },
    player_assists: { cats: ["assists"], order: 0 },
    player_saves: { cats: ["goalie"], pop: true, order: 0 },
    player_blocked_shots: { cats: ["other"], order: 0 },
    player_hits: { cats: ["other"], order: 1 },
  },
};

// A market a sport doesn't recognise still appears, under the safe "Other" category.
const FALLBACK_MAP_ENTRY = { cats: ["other"], order: 50 };

// Some provider feeds leak the MARKET name into the player field (e.g. a row whose "player" is
// literally "Anytime TD"). Those aren't real athletes and can't be graded, so they must never render
// as a player row. Detect and drop them defensively.
const JUNK_PLAYER = new Set([
  "anytime td", "anytime touchdown", "anytime touchdown scorer", "touchdown scorer", "td scorer",
  "first td", "first touchdown", "first touchdown scorer", "last td", "last touchdown", "last touchdown scorer",
  "anytime goal scorer", "goal scorer", "over", "under", "yes", "no", "total", "player", "any player", "field", "no scorer",
]);
function normName(s) { return String(s || "").toLowerCase().replace(/[^a-z0-9 ]/g, "").replace(/\s+/g, " ").trim(); }
export function isJunkPlayer(name) {
  const n = normName(name);
  if (!n) return true;
  if (JUNK_PLAYER.has(n)) return true;
  // a "name" that is only a market phrase (no real person), e.g. "anytime td", "1st touchdown", "td scorer"
  if (/^(anytime|first|last|1st|2nd|3rd|any)?\s*(td|touchdown|goal)s?(\s*scorer)?$/.test(n)) return true;
  return false;
}

export function normalizePropSport(sport = "") {
  const ns = normalizeSport(sport);
  return PROP_MAP[ns] ? ns : "default";
}

/* getPropCategory — the PRIMARY category key for a prop market (the first of its category set). */
export function getPropCategory(propType = "", sport = "") {
  const map = PROP_MAP[normalizeSport(sport)] || {};
  const e = map[propType] || FALLBACK_MAP_ENTRY;
  return (e.cats && e.cats[0]) || "other";
}

/* getPropCategories — ALL category keys a market belongs to (plus "popular" when flagged). */
export function getPropCategories(propType = "", sport = "") {
  const map = PROP_MAP[normalizeSport(sport)] || {};
  const e = map[propType] || FALLBACK_MAP_ENTRY;
  const cats = [...(e.cats || ["other"])];
  if (e.pop) cats.push("popular");
  return cats;
}

/* getPropMarketLabel — human label for a market. Prefers the provider-supplied prop_short so the
   wording matches exactly what the book posts; falls back to a Title-Cased prop_type. */
export function getPropMarketLabel(propType = "", propShort = "") {
  if (propShort) return propShort;
  return String(propType || "")
    .replace(/^player_/, "").replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase()).trim() || "Prop";
}

const isYesNo = (o) => o && (o.side === "yes" || o.side === "no");
const overOdds = (p) =>
  (p.standard && (p.standard.over?.odds ?? p.standard.yes?.odds)) ??
  (p.alts && p.alts[0] && p.alts[0].outcome.odds) ?? Number.MAX_SAFE_INTEGER;

/* altThresholdLabel — a provider over line rendered as its "N+" threshold (Over 49.5 → "50+"). */
export function altThresholdLabel(line) {
  const n = Number(line);
  if (!Number.isFinite(n)) return "";
  return `${Math.floor(n) + 1}+`;
}

/* buildPlayerMarket — for one player + one market, split the real outcomes into a standard O/U (or
   Yes/No) plus alternate thresholds. Never fabricates anything; carries the exact outcome objects. */
function buildPlayerMarket(player, image, outcomes) {
  const yesno = outcomes.some(isYesNo);
  if (yesno) {
    const yes = outcomes.find((o) => o.side === "yes") || null;
    const no = outcomes.find((o) => o.side === "no") || null;
    return { player, image, yesno: true, standard: { yesno: true, yes, no }, alts: [] };
  }
  // group by line
  const byLine = new Map();
  outcomes.forEach((o) => {
    const key = o.line == null ? "_" : String(o.line);
    const g = byLine.get(key) || { line: o.line, over: null, under: null };
    if (o.side === "under") g.under = o; else g.over = o;
    byLine.set(key, g);
  });
  const groups = [...byLine.values()];
  const twoSided = groups.filter((g) => g.over && g.under);
  // pick the most balanced two-sided line as the standard O/U (closest over/under prices)
  let standard = null;
  if (twoSided.length) {
    standard = twoSided.slice().sort((a, b) =>
      Math.abs((a.over.odds || 0) - (a.under.odds || 0)) - Math.abs((b.over.odds || 0) - (b.under.odds || 0))
    )[0];
  }
  // everything that isn't the chosen standard line becomes an alt threshold (real outcomes only)
  const alts = [];
  groups.forEach((g) => {
    if (standard && g.line === standard.line && g.over === standard.over) return;
    if (g.over) alts.push({ label: altThresholdLabel(g.over.line), side: "over", line: g.over.line, outcome: g.over });
    // keep a bettable under-only line visible too, labelled explicitly (rare)
    if (!g.over && g.under) alts.push({ label: `U ${g.under.line}`, side: "under", line: g.under.line, outcome: g.under });
  });
  alts.sort((a, b) => (Number(a.line) || 0) - (Number(b.line) || 0));
  return {
    player, image, yesno: false,
    standard: standard ? { line: standard.line, over: standard.over, under: standard.under } : null,
    alts,
  };
}

/* buildPropCatalog — the main transform. Returns ordered categories, each with ordered markets, each
   with ordered players. Only categories/markets that actually have outcomes are included. */
export function buildPropCatalog(players = [], sport = "") {
  const ns = normalizeSport(sport);
  const map = PROP_MAP[ns] || {};
  const catOrder = CATEGORY_ORDER[ns] || CATEGORY_ORDER.default;

  // 1) bucket outcomes by market (prop_type), then by player
  const markets = new Map(); // propType -> { propType, label, order, players: Map(player -> {image, outcomes[]}) }
  (players || []).forEach((p) => {
    if (isJunkPlayer(p.player)) return; // drop leaked market-name "players" like "Anytime TD"
    const image = (p.outcomes || []).find((o) => o.player_image_url)?.player_image_url || null;
    (p.outcomes || []).forEach((o) => {
      if (o.market && o.market !== "prop") return; // only player props
      const pt = o.prop_type || o.prop_short || "prop";
      let m = markets.get(pt);
      if (!m) {
        const entry = map[pt] || FALLBACK_MAP_ENTRY;
        m = { propType: pt, label: getPropMarketLabel(pt, o.prop_short), order: entry.order ?? 50, players: new Map() };
        markets.set(pt, m);
      }
      let pl = m.players.get(p.player);
      if (!pl) { pl = { player: p.player, image, outcomes: [] }; m.players.set(p.player, pl); }
      pl.outcomes.push(o);
    });
  });

  // 2) build per-market player rows
  const builtMarkets = [...markets.values()].map((m) => {
    const playerRows = [...m.players.values()]
      .map((pl) => buildPlayerMarket(pl.player, pl.image, pl.outcomes))
      .filter((pr) => pr.standard || (pr.alts && pr.alts.length)); // must have something bettable
    // order players: most likely/notable first (lower primary odds), then name — deterministic
    playerRows.sort((a, b) => {
      const oa = overOdds(a), ob = overOdds(b);
      if (oa !== ob) return oa - ob;
      return String(a.player).localeCompare(String(b.player));
    });
    const lineCount = playerRows.reduce((n, pr) => n + (pr.standard ? 1 : 0) + (pr.alts ? pr.alts.length : 0), 0);
    return { ...m, players: playerRows, playerCount: playerRows.length, lineCount };
  }).filter((m) => m.players.length);

  // 3) assemble categories (only those with markets)
  const catMap = new Map(); // key -> { key, label, markets: [] }
  builtMarkets.forEach((m) => {
    getPropCategories(m.propType, ns).forEach((catKey) => {
      let c = catMap.get(catKey);
      if (!c) { c = { key: catKey, label: CATEGORY_LABEL[catKey] || catKey, markets: [] }; catMap.set(catKey, c); }
      c.markets.push(m);
    });
  });
  // order markets within each category, then order categories per the sport table
  const categories = [...catMap.values()].map((c) => ({
    ...c,
    markets: c.markets.slice().sort((a, b) => (a.order - b.order) || a.label.localeCompare(b.label)),
    marketCount: c.markets.length,
    lineCount: c.markets.reduce((n, m) => n + m.lineCount, 0),
  }));
  categories.sort((a, b) => {
    const ia = catOrder.indexOf(a.key), ib = catOrder.indexOf(b.key);
    return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib) || a.label.localeCompare(b.label);
  });

  return { categories, markets: builtMarkets };
}

/* ---- Sibling-section model (FanDuel-style, category-contained) --------------------------------
   Pure and reference-preserving. They read an already-built market's player rows (each with
   `.standard` and `.alts`, carrying the EXACT provider outcome objects) and split one market into up
   to TWO sibling sections — a STANDARD O/U section and an ALT section — that render one after the
   other (standard first). Related markets stay together; there is NO global Alt category, NO Main/Alt
   toggle, and NO market-level threshold filter. Every player keeps ALL their real alternate
   thresholds inline. Nothing is fabricated, interpolated, combined, or mutated. */

/* getStandardSectionPlayers — players with a real standard line (two-sided O/U or Yes/No). Alt-only
   players are excluded so a standard section never renders empty rows for them. */
export function getStandardSectionPlayers(market) {
  return ((market && market.players) || []).filter((r) => r.standard);
}

/* getAltSectionPlayers — players that offer at least one real alternate threshold. Each row carries
   its `.alts` already numerically sorted (ascending by provider line) by buildPlayerMarket. */
export function getAltSectionPlayers(market) {
  return ((market && market.players) || []).filter((r) => r.alts && r.alts.length);
}

/* marketIsYesNo — a Yes/No or scorer market (Anytime TD, Anytime Goal Scorer…). Detected from the
   built rows, not the label string, so "O/U" is never wrongly appended to a Yes/No title. */
function marketIsYesNo(market) {
  return ((market && market.players) || []).some((r) => r.yesno || (r.standard && r.standard.yesno));
}

/* standardSectionLabel — the heading for a market's standard section. Two-sided O/U markets read as
   "<Market> O/U" (clearer intent); Yes/No & scorer markets keep their exact label (no "O/U"). */
export function standardSectionLabel(market) {
  const label = (market && market.label) || "Prop";
  if (marketIsYesNo(market)) return label;
  if (/\bO\/U\b/i.test(label)) return label;
  return `${label} O/U`;
}

/* altSectionLabel — the heading for a market's alt section. Preserves the FULL market context
   ("Receiving Yards" → "Alt Receiving Yards", "Pitcher Strikeouts" → "Alt Pitcher Strikeouts") —
   never truncates to a bare stat noun. */
export function altSectionLabel(market) {
  return `Alt ${(market && market.label) || "Lines"}`;
}

/* buildMarketSections — split one built market into its sibling sections, STANDARD first then ALT.
   A section is emitted ONLY when it has players, so a standard-only market yields one section and an
   alt-only market yields one section (never an empty counterpart). Stable keys `propType:standard` /
   `propType:alt` let siblings collapse independently and keep their open state across live refreshes.
   Each section's players carry the EXACT provider outcome objects unchanged. */
export function buildMarketSections(market) {
  if (!market) return [];
  const sections = [];
  const std = getStandardSectionPlayers(market);
  const alt = getAltSectionPlayers(market);
  if (std.length) {
    sections.push({
      key: `${market.propType}:standard`, type: "standard", propType: market.propType,
      label: standardSectionLabel(market), yesno: marketIsYesNo(market),
      players: std, playerCount: std.length,
    });
  }
  if (alt.length) {
    sections.push({
      key: `${market.propType}:alt`, type: "alt", propType: market.propType,
      label: altSectionLabel(market), yesno: false,
      players: alt, playerCount: alt.length,
    });
  }
  return sections;
}

/* buildCategorySections — flatten a category's already-ordered markets into a single ordered list of
   sibling sections. Because the markets arrive pre-sorted by PROP_MAP order, and each market emits
   its standard section immediately before its alt section, the result is naturally paired:

        <market 0> standard, <market 0> alt, <market 1> standard, <market 1> alt, …

   No fragile label sorting — ordering follows the base market order. */
export function buildCategorySections(markets = []) {
  const out = [];
  (markets || []).forEach((m) => buildMarketSections(m).forEach((s) => out.push(s)));
  return out;
}

/* buildPopularPreview — a small, high-value cross-section for the Event Detail "Popular" tab, drawn
   from the configured popular markets (one representative player row per popular market), NOT just the
   first N players. Returns an array of { market, label, row } entries, capped at `limit`. */
export function buildPopularPreview(players = [], sport = "", limit = 6) {
  const catalog = buildPropCatalog(players, sport);
  const popular = catalog.categories.find((c) => c.key === "popular");
  const source = popular ? popular.markets : catalog.markets;
  const out = [];
  for (const m of source) {
    if (m.players && m.players[0]) out.push({ market: m.propType, label: m.label, row: m.players[0] });
    if (out.length >= limit) break;
  }
  return out;
}
